import { config } from '../config.js';

/**
 * API Client для взаимодействия с backend
 */
class ApiClient {
    constructor() {
        this.baseUrl = config.apiBaseUrl;
        this.authHeaders = {};
        this.initAuth();
    }

    /**
     * Инициализация авторизации через Telegram WebApp
     */
    initAuth() {
        const initData = this.getInitData();

        if (initData) {
            // Production: используем initData от Telegram
            this.authHeaders = {
                'X-Telegram-Auth': initData,
            };
        } else if (config.devMode) {
            // Development fallback: используем X-Telegram-Id
            this.authHeaders = {
                'X-Telegram-Id': config.devTelegramId,
            };
        } else {
            console.error('[Auth Debug] Telegram WebApp not initialized and dev mode is off');
        }
    }

    /**
     * Получить initData из Telegram WebApp или из параметра tgWebAppData
     */
    getInitData() {
        if (window.Telegram?.WebApp?.initData) {
            return window.Telegram.WebApp.initData;
        }

        // Telegram передает initData в параметре tgWebAppData (hash) при открытии Mini App
        const hash = window.location.hash?.slice(1) || '';
        const params = new URLSearchParams(hash);
        const fromHash = params.get('tgWebAppData');
        if (fromHash) {
            return decodeURIComponent(fromHash);
        }

        return null;
    }

    /**
     * Базовый метод для HTTP запросов с retry logic
     */
    async request(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;
        const defaultOptions = {
            headers: {
                'Content-Type': 'application/json',
                ...this.authHeaders,
                ...options.headers,
            },
        };

        const finalOptions = { ...defaultOptions, ...options };

        let lastError;
        for (let attempt = 0; attempt < config.maxRetries; attempt++) {
            try {
                const response = await fetch(url, finalOptions);

                // Обработка 401 - необходимо обновить авторизацию
                if (response.status === 401) {
                    this.initAuth();
                    throw new Error('Unauthorized. Please refresh the app.');
                }

                // Обработка других ошибок
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(
                        errorData.detail ||
                        errorData.message ||
                        `HTTP Error: ${response.status}`
                    );
                }

                return await response.json();
            } catch (error) {
                lastError = error;
                console.error(`Attempt ${attempt + 1} failed:`, error.message);

                // Если это последняя попытка или ошибка 401, бросаем исключение
                if (attempt === config.maxRetries - 1 || error.message.includes('Unauthorized')) {
                    throw error;
                }

                // Exponential backoff
                await this.sleep(config.retryDelay * Math.pow(2, attempt));
            }
        }

        throw lastError;
    }

    /**
     * Утилита для задержки
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * POST /matchmaking/enqueue
     * Добавить игрока в очередь
     */
    async enqueue() {
        return this.request('/matchmaking/enqueue', {
            method: 'POST',
        });
    }

    /**
     * POST /matchmaking/leave
     * Покинуть очередь matchmaking
     */
    async leaveQueue() {
        return this.request('/matchmaking/leave', {
            method: 'POST',
        });
    }

    /**
     * POST /games/{game_id}/vote
     * Проголосовать за подозреваемого бота
     * @param {string} gameId - ID игры
     * @param {string|null} suspectedPlayerId - ID подозреваемого игрока или null
     */
    async vote(gameId, suspectedPlayerId) {
        return this.request(`/games/${gameId}/vote`, {
            method: 'POST',
            body: JSON.stringify({
                suspected_player_id: suspectedPlayerId,
            }),
        });
    }

    /**
     * GET /games/{game_id}/results
     * Получить результаты игры
     * @param {string} gameId - ID игры
     */
    async getResults(gameId) {
        return this.request(`/games/${gameId}/results`, {
            method: 'GET',
        });
    }

    /**
     * GET /users/me
     * Получить профиль текущего пользователя
     */
    async getUserProfile() {
        return this.request('/users/me', {
            method: 'GET',
        });
    }

    /**
     * GET /leaderboard
     * Получить таблицу лидеров
     * @param {string} [league] - фильтр по лиге
     */
    async getLeaderboard(league) {
        let url = '/leaderboard';
        if (league) {
            url += `?league=${league}`;
        }
        return this.request(url, {
            method: 'GET',
        });
    }

    /**
     * GET /settings
     * Получить настройки пользователя
     */
    async getSettings() {
        return this.request('/settings', {
            method: 'GET',
        });
    }

    /**
     * PATCH /settings
     * Обновить настройки пользователя
     * @param {Object} settings - { language: 'en'|'ru'|'uk', vibration_enabled: boolean }
     */
    async updateSettings(settings) {
        return this.request('/settings', {
            method: 'PATCH',
            body: JSON.stringify(settings),
        });
    }
}

// Экспортируем singleton instance
export const apiClient = new ApiClient();
